import { defineStore } from 'pinia'
import { ref } from 'vue'
import { ElMessage } from 'element-plus'
import * as golfApi from '@/api/golf'
import { extractErrorMessage } from '@/helpers/error-parsers'
import type { GolfUser } from '@/api/golf'
import type { PaginationMeta } from '@/models/api'

export const useGolfUsersStore = defineStore('golf-users', () => {
  const users = ref<GolfUser[]>([])
  const usersMeta = ref<PaginationMeta | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const fetchUsers = async (filters?: {
    status_golf?: string
    q?: string
    page?: number
    per_page?: number
  }) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.getGolfUsers(filters)
      const responseData = response.data as any
      if (responseData && responseData.data && Array.isArray(responseData.data)) {
        users.value = responseData.data
        usersMeta.value = responseData.meta || null
      } else if (Array.isArray(responseData)) {
        users.value = responseData
        usersMeta.value = null
      } else {
        users.value = []
        usersMeta.value = null
      }
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to fetch users')
      console.error('Error fetching users:', e)
      users.value = []
      usersMeta.value = null
    } finally {
      isLoading.value = false
    }
  }

  const fetchUser = async (id: string) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.getGolfUser(id)
      const responseData = response.data.data as any
      if (responseData && responseData.data) {
        return responseData.data as GolfUser
      }
      return (responseData as GolfUser) || null
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to fetch user')
      console.error('Error fetching user:', e)
      return null
    } finally {
      isLoading.value = false
    }
  }

  const updateUserStatus = async (
    id: string,
    statusGolf: 'active' | 'inactive'
  ) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.updateGolfUserStatus(id, statusGolf)
      const updated = response.data.data
      if (updated) {
        const index = users.value.findIndex(u => u.id === id)
        if (index !== -1) {
          users.value[index] = { ...users.value[index], ...updated }
        }
        ElMessage.success(
          `User access ${statusGolf === 'active' ? 'granted' : 'revoked'}`
        )
        return updated
      }
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to update user status')
      ElMessage.error({
        message: error.value,
        duration: 6000,
        showClose: true,
      })
      console.error('Error updating user status:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  const resetError = () => {
    error.value = null
  }

  return {
    users,
    usersMeta,
    isLoading,
    error,
    fetchUsers,
    fetchUser,
    updateUserStatus,
    resetError,
  }
})
